<script setup>
  import { ref, reactive, onMounted, computed, inject, defineEmits, onUnmounted } from 'vue'
  import { storeToRefs } from 'pinia'
  import BcsModal from '../BcsModal.vue'
  import BcsWizardSteps from '../common/BcsWizardSteps.vue'
  import { useEmployerStore } from '../../stores/EmployerStore'
  import { useRenewalEmployerStore } from '../../stores/RenewalEmployerStore'
  import PlanQuestionnaireForm from '../PlanQuestionnaire/PlanQuestionnaireForm.vue'
  import EmployerBasicInfoForm from '../common/EmployerBasicInfoForm.vue'
  import ConfirmRenewalStep from './ConfirmRenewalStep.vue'
  import RenewalSuccessMessage from './RenewalSuccessMessage.vue'
  import RenewalHelpGuide from './RenewalHelpGuide.vue'
  import { find, isEmpty, pick, get as lodashGet, filter } from 'lodash'
  import { calculateGroupInsuranceTypeId } from '../../utils'
  import { getAccountOwners } from '../../utils/accountOwnersHelpers'
  import { useSystemParamsStore } from '../../stores/SystemParamsStore'
  import { RequestService } from '../../services/RequestService'
  import { AgencyDataService } from '../../services/AgencyDataService'
  import { getDatabase, ref as dbRef, set, get, remove } from 'firebase/database'
  import { getAuth } from 'firebase/auth'
  import BcsDynamicText from '../BcsDynamicText.vue'

  const emitter = inject('emitter')
  const functionsBaseUrl = inject('functionsBaseUrl')
  const emit = defineEmits(['refreshHomeData'])
  
  // Firebase instances
  const db = getDatabase()
  const auth = getAuth()

  const modalRef = ref(null)
  const wizardStepsRef = ref(null)
  const currentStep = ref(0)
  const selectionMode = ref(false)
  const employerStore = useEmployerStore()
  const renewalStore = useRenewalEmployerStore()
  const selectedEmployerId = ref(null)
  const planYearIdCreated = ref(null)
  const planForm = ref([])
  const formIsValid = ref(false)
  const isOpen = ref(false)
  const renewalSuccess = ref(false)
  const showHelpGuide = ref(false)
  const lastPlanStartDate = ref('')
  const lastPlanYearId = ref(null)
  const currentPlanYearId = ref(null)
  const currentPlanStartDate = ref('')
  const isFromDuplicateButton = ref(false)
  // Get questions from the store
  const { planQuestions, mainAssessKey } = storeToRefs(renewalStore)
  const { allDistinctEmployers, userData, isLoadingAllDistinctEmployers, actualEmployer } = storeToRefs(employerStore)
  const { getPlanYearsOfEmployer, getEmployer, getAllDistinctEmployers } = employerStore
  const { savePlanQuestionsOnRenewal, loadQuestionsAndAnswers, resetRenewalStore } = renewalStore
  const { put, handleErrors, createLog } = RequestService()

  // Load questions when employer is selected
  const loadQuestions = async () => {
    if (lastPlanYearId.value) {
      await loadQuestionsAndAnswers(lastPlanYearId.value)
      planForm.value = planQuestions.value[mainAssessKey.value]
    }
  }
  
  // Default values for employer data
  const defaultEmployerValues = {
    id: null,
    name: '',
    accountOwners: [],
    ein: '',
    plan_start_date: '',
    contact_name: '',
    contact_emails: ''
  }
  
  // Employer data reactive object
  const employerData = reactive({...defaultEmployerValues})

  // Computed property to determine the step numbers
  const stepNumbers = computed(() => {
    if (selectionMode.value) {
      return {
        selectEmployer: 0,
        confirmBasicInfo: 1,
        renewalPlan: 2,
        confirmRenewal: 3
      }
    } else {
      return {
        confirmBasicInfo: 0,
        renewalPlan: 1,
        confirmRenewal: 2
      }
    }
  })

  // Computed property for wizard steps to pass to BcsWizardSteps
  const wizardSteps = computed(() => {
    // Base steps that are always shown
    const baseSteps = [
      { 
        title: 'Confirm Basic Info',
        status: currentStep.value > stepNumbers.value.confirmBasicInfo ? 'completed' : 
                currentStep.value === stepNumbers.value.confirmBasicInfo ? 'active' : 'empty'
      },
      {
        title: 'Renewal Plan',
        status: currentStep.value > stepNumbers.value.renewalPlan ? 'completed' : 
                currentStep.value === stepNumbers.value.renewalPlan ? 'active' : 'empty'
      },
      {
        title: 'Confirm Renewal',
        status: currentStep.value === stepNumbers.value.confirmRenewal ? 'active' : 'empty'
      }
    ]
    
    // If in selection mode, add the first step
    if (selectionMode.value) {
      return [
        {
          title: 'Choose Employer',
          status: currentStep.value > stepNumbers.value.selectEmployer ? 'completed' : 
                  currentStep.value === stepNumbers.value.selectEmployer ? 'active' : 'empty'
        },
        ...baseSteps
      ]
    }
    
    return baseSteps
  })

  // Get button text based on current step
  const getButtonText = computed(() => {
    // If we're at the last step (confirm renewal)
    if (currentStep.value === stepNumbers.value.confirmRenewal) {
      return 'Confirm Renewal'
    }
    
    // If we're in selection mode and at the select employer step
    if (selectionMode.value && currentStep.value === stepNumbers.value.selectEmployer) {
      return 'Continue'
    }
    
    // For all other steps
    return 'Save and Continue'
  })

  function addOneYear(dateStr) {
    // Split the input string into month, day, and year
    const parts = dateStr.split("/");
    const month = parseInt(parts[0], 10) - 1; // JavaScript months are 0-indexed
    const day = parseInt(parts[1], 10);
    const year = parseInt(parts[2], 10);

    // Create a Date object from the input components
    const date = new Date(year, month, day);

    // Add one year to the date
    date.setFullYear(date.getFullYear() + 1);

    // Format the new date back to "MM/DD/YYYY"
    const newMonth = String(date.getMonth() + 1).padStart(2, '0'); // add 1 to get the proper month number
    const newDay = String(date.getDate()).padStart(2, '0');
    const newYear = date.getFullYear();

    return `${newMonth}/${newDay}/${newYear}`;
  }

  // Open the modal in direct mode (when an employer is already selected)
  const openModal = async (data = {}) => {
    // Reset the form first
    resetForm()
    if (!modalRef.value) return
    
    // Check if there's saved progress
    if (auth.currentUser && !data.isFromDuplicateButton) {
      const progressRef = dbRef(db, `TEMP/RENEWAL_PROGRESS/${auth.currentUser.uid}`)
      const snapshot = await get(progressRef)
      
      // If there's saved progress, use it
      if (snapshot.exists()) {
        const savedProgress = snapshot.val()
        const progressLoaded = await loadSavedProgress(savedProgress, data)
        if (progressLoaded) {
          // Open the modal
          if (modalRef.value) {
            modalRef.value.openModal()
            isOpen.value = true
            
            // After modal is opened, recalculate positions and add resize listener
            if (wizardStepsRef.value) {
              // Add resize listener and recalculate positions
              wizardStepsRef.value.addResizeListener()
              wizardStepsRef.value.recalculatePositions()
            }
          }
          return
        }
      }
    }
    
    // If we have employer data, we're in direct mode (no selection needed)
    if (data && data.id) {
      selectionMode.value = false

      if (data.plan_start_date) {        
        const newPlanStartDate = addOneYear(data.plan_start_date);
        lastPlanStartDate.value = data.plan_start_date
        lastPlanYearId.value = data.planYearId
        currentPlanYearId.value = data.currentPlanYearId
        currentPlanStartDate.value = data.currentPlanStartDate || ''
        employerData.plan_start_date = newPlanStartDate;
        isFromDuplicateButton.value = data.isFromDuplicateButton || false
      }

      employerData.id = data.id
      employerData.name = data.name || ''
      employerData.accountOwners = getAccountOwners(data)
      employerData.ein = data.ein || ''
      employerData.contact_name = data.contact_name || ''
      employerData.contact_emails = data.contact_emails || ''
      
      // Load questions for this employer
      await loadQuestions()
    } else {
      // Otherwise, we need to show the employer selection step
      selectionMode.value = true
    }
    
    // Open the modal
    if (modalRef.value) {
      modalRef.value.openModal()
      isOpen.value = true
      
      // After modal is opened, recalculate positions and add resize listener
      if (wizardStepsRef.value) {
        // Add resize listener and recalculate positions
        wizardStepsRef.value.addResizeListener()
        wizardStepsRef.value.recalculatePositions()
      }
    }
  }

  function closeModal() {
    // Remove resize listener when modal is closed to prevent memory leaks
    if (wizardStepsRef.value) {
      wizardStepsRef.value.removeResizeListener()
    }
    
    // Close the modal
    if (modalRef.value) {
      modalRef.value.closeModal()
      isOpen.value = false
    }
    
    // Reset the form
    resetForm()
    
    // Reset success state
    renewalSuccess.value = false
    showHelpGuide.value = false
  }

  function resetForm() {
    // Reset employer data to default values
    Object.assign(employerData, defaultEmployerValues)
    
    // Reset form state
    formIsValid.value = false
    selectedEmployerId.value = null
    currentStep.value = 0
    selectionMode.value = false
    lastPlanStartDate.value = ''
    lastPlanYearId.value = null
    currentPlanYearId.value = null
    isFromDuplicateButton.value = false
    resetRenewalStore()
  }

  const isNextStepLoading = ref(false)

  async function goToNextStep() {
    if (isNextStepLoading.value) return
    isNextStepLoading.value = true
    try {
      // Save progress when the button text is 'Save and Continue' and not from duplicate button
      if (getButtonText.value === 'Save and Continue' && !isFromDuplicateButton.value) {
        await saveProgress()
      }
      
      // If we're in selection mode and at the select employer step
      if (selectionMode.value && currentStep.value === stepNumbers.value.selectEmployer) {
        // Find the selected employer
        const selectedEmployer = find(allDistinctEmployers.value, (e => e.id === selectedEmployerId.value))
        if (!selectedEmployer) {
          closeModal()
          return
        }
        
        // Set the employer data
        if (selectedEmployer.plan_start_date) {        
          const newPlanStartDate = addOneYear(selectedEmployer.plan_start_date)
          lastPlanStartDate.value = selectedEmployer.plan_start_date
          employerData.plan_start_date = newPlanStartDate;
        }
        lastPlanYearId.value = selectedEmployer.planYearId
        employerData.id = selectedEmployer.id
        employerData.name = selectedEmployer.name || ''
        employerData.accountOwners = getAccountOwners(selectedEmployer)
        employerData.ein = selectedEmployer.ein || ''
        employerData.contact_name = selectedEmployer.contact_name || ''
        employerData.contact_emails = selectedEmployer.contact_emails || ''
      }
      if (currentStep.value === stepNumbers.value.confirmBasicInfo) {
        // Load questions for this employer
        const updatedEmployer = await saveEmployerData(false)
        if (lodashGet(updatedEmployer, 'employerBasicInfoUpdated') && !selectionMode.value) {
          await getEmployer(lodashGet(actualEmployer.value, 'planYearId'), true)
        } else if (selectionMode.value && lodashGet(updatedEmployer, 'employerBasicInfoUpdated')) {
          await getAllDistinctEmployers(true)
        }
        await loadQuestions()
      }
      
      // If we're at the last step, handle form submission
      if (currentStep.value === wizardSteps.value.length - 1) {
        // Handle form submission
        await handleSubmitRenewal();      
        return;
      }
      
      // Otherwise, go to the next step
      currentStep.value++
      
      // After changing step, recalculate positions
      if (wizardStepsRef.value) {
        wizardStepsRef.value.recalculatePositions()
      }
    } finally {
      isNextStepLoading.value = false
    }
  }

    // Helper to derive group_insurance_type directly from current planForm answers
  function getGroupInsuranceTypeId () {
    const getAnswer = (questionId) => {
      const q = planForm.value.find(q => q.id === questionId)
      if (!q) return null
      let a = q.answer
      if (typeof a === 'object') {
        a = a.value ?? a
      }
      return a
    }
    const fundingType = getAnswer('11')
    const noEmployees = getAnswer('9')
    const { largeGroupCutoff } = useSystemParamsStore()
    return calculateGroupInsuranceTypeId(noEmployees, fundingType, largeGroupCutoff)
  }

  const saveEmployerData = async (isRenewal = false) => {
  const groupInsuranceType = getGroupInsuranceTypeId()
    let response = await put(
      'updateEmployer',
      `${functionsBaseUrl}/employerGateway/api/v1/employers/${employerData.id}`,
      {
        agency_id: userData.value.agencyId,
        isRenewal,
        id: employerData.id,
        name: employerData.name,
        accountOwners: employerData.accountOwners || [],
        ein: employerData.ein,
        plan_start_date: employerData.plan_start_date,
        contact_name: employerData.contact_name,
        contact_emails: employerData.contact_emails,
        groupInsuranceType
      }
    )
    const updatedEmployer = lodashGet(response, 'data.data')
    return updatedEmployer
  }

  // Handle the final submission of the renewal
  async function handleSubmitRenewal() {
    // Hide the wizard steps and footer    
    const updatedEmployer = await saveEmployerData(true)
    if (updatedEmployer) {
      await savePlanQuestionsOnRenewal(updatedEmployer.planYearId, planForm.value)
      emit('refreshHomeData', { showLoading: false, refreshAll: false });
    
      if (updatedEmployer.employerBasicInfoUpdated) {
        const EMPLOYER_INFORMATION_UPDATED = '8'
        createLog({
          eventId: EMPLOYER_INFORMATION_UPDATED,
          logName: 'user_activity_log_v2',
          employerId: employerData.id,
          planYearId: updatedEmployer.planYearId,
          agencyId: userData.value.agencyId
        }).catch((err) => handleErrors('createLog employer information updated', err))
      }
      planYearIdCreated.value = updatedEmployer.planYearId
      
      // Save the newly created plan year ID to localStorage to show notification on first visit
      localStorage.setItem(`newPlanYear_dashboard_${updatedEmployer.planYearId}`, 'true')
      localStorage.setItem(`newPlanYear_plan_${updatedEmployer.planYearId}`, 'true')
      
      // Clear saved progress since we've completed the renewal successfully
      await clearSavedProgress()
      
      renewalSuccess.value = true;
      if (!selectionMode.value && !isFromDuplicateButton.value) {
        getPlanYearsOfEmployer(employerData.id, userData.value.agencyId, true)
      }

      // Mark agency data as updated for Action Center cache invalidation
      const { markAgencyDataUpdated } = AgencyDataService()
      markAgencyDataUpdated(userData.value.agencyId)
    }
  }

  // Save progress to Firebase Realtime Database
  const saveProgress = async () => {
    if (!auth.currentUser) return
    
    try {
      // Create a reference to the user's progress in the TEMP node
      const progressRef = dbRef(db, `TEMP/RENEWAL_PROGRESS/${auth.currentUser.uid}`)
      
      // Create an object with all the data we need to restore the modal state
      const progressData = {
        timestamp: Date.now(),
        currentStep: currentStep.value,
        selectionMode: selectionMode.value,
        selectedEmployerId: selectedEmployerId.value || null,
        employerData: pick(employerData, ['plan_start_date']),
        formIsValid: formIsValid.value,
        lastPlanStartDate: lastPlanStartDate.value || null,
        lastPlanYearId: lastPlanYearId.value,
        currentPlanYearId: currentPlanYearId.value || null,
        currentPlanStartDate: currentPlanStartDate.value || null
      }
      if (currentStep.value === stepNumbers.value.renewalPlan) {
        Object.assign(progressData, { planForm: JSON.parse(JSON.stringify(planForm.value)) })
      }
      
      // Save the data to Firebase
      await set(progressRef, progressData)
    } catch (error) {
      handleErrors('Error saving renewal progress:', error)
    }
  }
  
  // Load saved progress from Firebase
  const loadSavedProgress = async (savedProgress = {}, data = {}) => {
    if (!auth.currentUser) return
    
    try { 
      if (!isEmpty(savedProgress)) {
        // Eval if is selection mode but the employer data is provided by employer dashboard
        const savedSelectionMode = lodashGet(savedProgress, 'selectionMode', false)
        const savedLastPlanYearId = lodashGet(savedProgress, 'lastPlanYearId', null)
        const currentLastPlanYearId = lodashGet(data, 'planYearId', null)
        
        if (savedSelectionMode !== isEmpty(data) || (!savedSelectionMode && savedLastPlanYearId !== currentLastPlanYearId)) {
          await clearSavedProgress()
          return false
        }
        
        // Restore the modal state
        currentStep.value = savedProgress.currentStep
        selectionMode.value = savedProgress.selectionMode
        selectedEmployerId.value = savedProgress.selectedEmployerId
        
        // Restore employer data
        if (!selectionMode.value && !isEmpty(data)) {
          Object.assign(employerData, data)
        } else if (selectionMode.value && selectedEmployerId.value) {
          const selectedEmployer = find(allDistinctEmployers.value, (e => e.id === selectedEmployerId.value))
          Object.assign(employerData, selectedEmployer)
        }

        // Restore saved plan start date from employer data
        Object.assign(employerData, savedProgress.employerData)
        
        formIsValid.value = savedProgress.formIsValid
        lastPlanStartDate.value = savedProgress.lastPlanStartDate
        lastPlanYearId.value = savedProgress.lastPlanYearId
        currentPlanYearId.value = savedProgress.currentPlanYearId
        currentPlanStartDate.value = savedProgress.currentPlanStartDate

        // Restore plan form
        if (currentStep.value === stepNumbers.value.renewalPlan && savedProgress.planForm) {
          Object.assign(planForm.value, savedProgress.planForm)
        }
        
        return true
      }
      
      return false
    } catch (error) {
      handleErrors('Error loading renewal progress:', error)
      return false
    }
  }
  
  // Clear saved progress when modal is closed or cancelled
  const clearSavedProgress = async () => {
    if (!auth.currentUser) return
    
    try {
      const progressRef = dbRef(db, `TEMP/RENEWAL_PROGRESS/${auth.currentUser.uid}`)
      await remove(progressRef)
    } catch (error) {
      handleErrors('Error clearing renewal progress:', error)
    }
  }

  const handleCancel = () => {
    clearSavedProgress()
    closeModal()
  }

  // Function to show the help guide
  const showRenewalHelpGuide = () => {
    showHelpGuide.value = true
  }

  // Function to hide the help guide and return to the modal
  const hideRenewalHelpGuide = () => {
    showHelpGuide.value = false
  }

  // Listen for global event to open the modal and cleanup on unmount
  const openRenewModalHandler = data => {
    openModal(data)
  }
  onMounted(() => {
    emitter.on('open-renew-employer-modal', openRenewModalHandler)
  })
  onUnmounted(() => {
    emitter.off('open-renew-employer-modal', openRenewModalHandler)
  })
</script>

<template>
  <BcsModal
    ref="modalRef"
    modalSize="small"
    :modalType="'confirmation'"
    :hideCloseButton="true"
    :showFooter="false"
    :noDefaultStyles="true"
    @closeButtonClicked="closeModal">
    <template #header>
      <div class="w-100 px-4 pt-3" v-if="!renewalSuccess">
        <div class="d-flex justify-content-between align-items-center pt-0 mb-1">
          <span class="modal-title header-22"><strong>Renew {{employerData.name || "an Employer"}}</strong></span>
          <button type="button" class="btn-close" @click="closeModal" aria-label="Close"></button>
        </div>
        <div v-if="!showHelpGuide">
          <p class="text-start text-14">
            <span v-if="selectionMode">
              <BcsDynamicText text="selectionModeHeaderText" key="selectionModeHeaderText"/>
            </span>
            <span v-else>
              <BcsDynamicText text="empDashboardHeaderText" key="empDashboardHeaderText"/>
            </span>
          </p>
        </div>
      </div>
    </template>
    <template #body-header>
      <div v-if="!renewalSuccess && !showHelpGuide" class="mb-3.5 mt-4.5 px-4">
        <BcsWizardSteps 
          ref="wizardStepsRef"
          :steps="wizardSteps" 
          :activeStep="currentStep"
        />
      </div>
    </template>

    <!-- Help Guide -->
    <div v-if="showHelpGuide">
      <RenewalHelpGuide @back="hideRenewalHelpGuide" />
    </div>

    <!-- Step: Select Employer (only shown in selection mode) -->
    <div v-if="selectionMode && currentStep === stepNumbers.selectEmployer && !showHelpGuide" class="text-start px-0 mx-0 employer-list">
      <span class="mb-3 header-20 ps-4.5"><strong class="ps-2">
        <BcsDynamicText text="selectEmployerText" key="selectEmployerText" />
      </strong></span>
      <div v-if="isLoadingAllDistinctEmployers" class="text-center py-4">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
        <p class="mt-2">Loading employers...</p>
      </div>
      
      <div v-else-if="allDistinctEmployers.length === 0" class="alert alert-info text-center">
        <BcsDynamicText text="noEmployersFoundText" key="noEmployersFoundText" />
      </div>
      
      <div v-else class=" ps-4.5 mt-3">
        <div v-for="employer in filter(allDistinctEmployers, { is_plan_complete: true })" :key="employer.id" class="form-check py-2">
          <input 
            class="form-check-input me-2" 
            type="radio" 
            :id="`employer-${employer.id}`" 
            :value="employer.id" 
            v-model="selectedEmployerId"
          >
          <label class="form-check-label text-16 d-inline" :for="`employer-${employer.id}`">
            {{ employer.name }}
          </label>
        </div>
      </div>
    </div>

    <!-- Step: Confirm Basic Info -->
    <div v-if="currentStep === stepNumbers.confirmBasicInfo && isOpen && !showHelpGuide" class="text-start px-4">
      <h5 class="mb-3 fw-bold">Please confirm {{ employerData.name }}'s basic information:</h5>

      <EmployerBasicInfoForm
        :employerData="employerData"
        :hideEmployerName="false"
        :lastPlanStartDate="lastPlanStartDate"
        :isEditMode="true"
        @update="(formState) => {
          Object.assign(employerData, formState.values);
          formIsValid = formState.isValid;
        }"
      />
    </div>

    <!-- Step: Renewal Plan -->
    <div v-if="currentStep === stepNumbers.renewalPlan && !showHelpGuide" class="text-start px-4 renewal-plan">
      <p class="header-20 mb-4"><strong>
        <BcsDynamicText text="planFormText" key="planFormText" />
      </strong></p>
      
      <PlanQuestionnaireForm
        :questions="planForm"
        :autoSave="false"
        :isRenewPlanYearForm="true"
      />
    </div>

    <!-- Step: Confirm Renewal -->
    <div v-if="currentStep === stepNumbers.confirmRenewal && !renewalSuccess && !showHelpGuide" class="text-start px-4">
      <ConfirmRenewalStep :plan-start-date="employerData.plan_start_date" />
    </div>

    <!-- Success Message (shown after confirmation) -->
    <div v-if="renewalSuccess && !showHelpGuide">
      <RenewalSuccessMessage 
        :plan-start-date="employerData.plan_start_date"
        :employer-name="employerData.name"
        :plan-year-id="planYearIdCreated"
        :selection-mode="selectionMode"
        :current-plan-start-date="currentPlanStartDate"
        :current-plan-year-id="currentPlanYearId"
        :is-from-duplicate-button="isFromDuplicateButton"
        :actual-employer="actualEmployer"
        @close="closeModal"
      />
    </div>
    
    <!-- Footer -->
    <template #footer-body>
      <div v-if="!renewalSuccess && !showHelpGuide" class="d-flex justify-content-between align-items-center px-3.5 py-2.5">
        <div>
          <i class="bi bi-info-circle-fill text-primary ms-1 me-1"></i>
          <a
            href="#"
            class="text-primary me-3 text-decoration-none text-16"
            @click.prevent="showRenewalHelpGuide"
          >
            <med>
              How does renewal work?
            </med>
          </a>
        </div>
        <div>
          <button
            id="cancelRenewEmployer"
            type="button"
            class="btn btn-info px-2"
            @click="handleCancel">
            <med> 
              Cancel
            </med>
          </button>
          <button
            id="nextStepRenewEmployer"
            type="button"
            class="btn btn-primary ms-2 me-1 px-3"
            @click="goToNextStep"
            :disabled="isNextStepLoading || (selectionMode && currentStep === stepNumbers.selectEmployer && !selectedEmployerId) || (currentStep === stepNumbers.confirmBasicInfo && !formIsValid)">
            {{ getButtonText }} <i class="bi bi-chevron-compact-right"></i>
          </button>
        </div>
      </div>
    </template>
  </BcsModal>
</template>

<style scoped>
.employer-list {
  max-height: 400px;
  overflow-y: auto;
}

.renewal-plan {
  max-height: 400px;
  overflow-y: auto;
}

@media (max-width: 576px) {
  .step-text {
    font-size: 0.65rem;
    max-width: 70px;
  }
}
</style>
